import { useId, useState } from 'react';
import { Plus } from 'lucide-react';
import { displayName, searchIngredients, type IngredientId } from '../domain/taxonomy';
import { MAX_QUERY, sanitizeInput } from '../domain/text';

/**
 * Autocomplete over the canonical taxonomy. Only taxonomy items can reach recipes; when
 * `allowCustom` is set (grocery list) free text is accepted as a non-matching custom item.
 */
export function IngredientPicker({
  onPick, onCustom, placeholder = 'Add an item…', exclude = [], label = 'Add an item', autoFocus,
}: {
  onPick: (id: IngredientId) => void;
  onCustom?: (text: string) => void;
  placeholder?: string;
  exclude?: IngredientId[];
  label?: string;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const listId = useId();
  const results = searchIngredients(q, 8).filter((id) => !exclude.includes(id));
  const pick = (id: IngredientId) => { onPick(id); setQ(''); };
  const submit = () => {
    if (results[0]) pick(results[0]);
    else if (onCustom && q.trim()) { onCustom(q.trim()); setQ(''); }
  };
  return (
    <div>
      <label className="sr-only" htmlFor={`${listId}-input`}>{label}</label>
      <div className="flex gap-2">
        <input id={`${listId}-input`} className="input flex-1" value={q} placeholder={placeholder} maxLength={MAX_QUERY}
          autoFocus={autoFocus} autoComplete="off" enterKeyHint="done"
          role="combobox" aria-expanded={results.length > 0} aria-controls={listId} aria-autocomplete="list"
          onChange={(e) => setQ(sanitizeInput(e.target.value, MAX_QUERY))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
        <button className="btn btn-tonal px-4" onClick={submit} disabled={!q.trim()} aria-label="Add">
          <Plus size={20} aria-hidden />
        </button>
      </div>
      {q.trim() && (
        <ul id={listId} role="listbox" className="mt-2 flex flex-wrap gap-2">
          {results.map((id) => (
            <li key={id} role="option" aria-selected={false}>
              <button className="chip" onClick={() => pick(id)}>{displayName(id)}</button>
            </li>
          ))}
          {results.length === 0 && (
            <li className="text-sm text-fg-muted py-2">
              {onCustom ? <>No match. Press add to keep “{q.trim()}” as a custom item.</> : <>We don’t know “{q.trim()}” yet, so it can’t be used for recipes.</>}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
